/**
 * Route Territory Map Viewer
 *
 * SVG renderer for route-map and territory-map visual artifacts.
 *
 * Primary responsibilities:
 * - Fit route stops, paths, and service territories into one viewport
 * - Render SVG routes, markers, territory fills, labels, and metrics
 * - Prepare MapLibre and deck.gl compatible data without importing map engines
 *
 * Dependencies:
 * - React
 * - Geo tile map helpers
 */

'use client'

import { useEffect, useMemo } from 'react'
import {
  GeoTileLayer,
  createGeoTileMap,
  getGeoBounds,
  projectGeoCoordinate,
  type GeoTileMap
} from '@/components/visual-artifacts/geo-tile-map'

export type LngLatTuple = [longitude: number, latitude: number]

export type RouteTerritoryCoordinate =
  | {
      latitude: number
      longitude: number
    }
  | {
      lat: number
      lng: number
    }
  | LngLatTuple

export type RouteStop = {
  id: string
  label: string
  coordinate: RouteTerritoryCoordinate
  address?: string
  sequence?: number
  status?: string
  eta?: string
  confidence?: number
  note?: string
}

export type RoutePath = {
  id?: string
  label?: string
  coordinates?: RouteTerritoryCoordinate[]
  path?: RouteTerritoryCoordinate[]
  stopIds?: string[]
  color?: string
  distanceKm?: number
  distanceMeters?: number
  durationMinutes?: number
  durationSeconds?: number
  source?: string
  confidence?: number
}

export type ServiceTerritory = {
  id: string
  label: string
  coordinates: RouteTerritoryCoordinate[]
  color?: string
  fill?: string
  stroke?: string
  areaSqKm?: number
  intensity?: number
  owner?: string
  metric?: {
    label: string
    value: number
    unit?: string
  }
  source?: string
  confidence?: number
}

export type RouteTerritoryMetric = {
  label: string
  value: string | number
  unit?: string
}

export type RouteTerritoryMetricMap = Record<string, string | number>

export type RouteTerritorySourceNote = {
  label?: string
  source?: string
  note?: string
  confidence?: number
}

export type RouteMapData = {
  center?: RouteTerritoryCoordinate
  stops?: RouteStop[]
  route?: RouteTerritoryCoordinate[]
  routes?: RoutePath[]
  paths?: RoutePath[]
  territories?: ServiceTerritory[]
  polygons?: ServiceTerritory[]
  markers?: RouteStop[]
  summary?: RouteTerritoryMetric[] | RouteTerritoryMetricMap
  metrics?: RouteTerritoryMetric[] | RouteTerritoryMetricMap
  sources?: RouteTerritorySourceNote[]
  sourceNotes?: string[]
  source?: string
  confidence?: number
  notes?: string[]
}

export type TerritoryMapData = {
  center?: RouteTerritoryCoordinate
  stops?: RouteStop[]
  routes?: RoutePath[]
  paths?: RoutePath[]
  territories?: ServiceTerritory[]
  polygons?: ServiceTerritory[]
  markers?: RouteStop[]
  summary?: RouteTerritoryMetric[] | RouteTerritoryMetricMap
  metrics?: RouteTerritoryMetric[] | RouteTerritoryMetricMap
  sources?: RouteTerritorySourceNote[]
  sourceNotes?: string[]
  source?: string
  confidence?: number
  notes?: string[]
}

export type RouteTerritoryMapArtifact =
  | {
      id: string
      type: 'route-map'
      title: string
      createdAt?: string
      data: RouteMapData
    }
  | {
      id: string
      type: 'territory-map'
      title: string
      createdAt?: string
      data: TerritoryMapData
    }

export type GeoJsonPointFeature = {
  type: 'Feature'
  geometry: {
    type: 'Point'
    coordinates: LngLatTuple
  }
  properties: {
    id: string
    label: string
    sequence?: number
    status?: string
    confidence?: number
  }
}

export type GeoJsonPathFeature = {
  type: 'Feature'
  geometry: {
    type: 'LineString'
    coordinates: LngLatTuple[]
  }
  properties: {
    id: string
    label: string
    color: string
    distanceKm?: number
    durationMinutes?: number
    confidence?: number
  }
}

export type GeoJsonPolygonFeature = {
  type: 'Feature'
  geometry: {
    type: 'Polygon'
    coordinates: LngLatTuple[][]
  }
  properties: {
    id: string
    label: string
    fill: string
    stroke: string
    areaSqKm?: number
    confidence?: number
  }
}

export type GeoJsonFeatureCollection<Feature> = {
  type: 'FeatureCollection'
  features: Feature[]
}

export type DeckStopDatum = {
  id: string
  label: string
  position: LngLatTuple
  color: RgbTuple
  radiusMeters: number
  confidence?: number
}

export type DeckRouteDatum = {
  id: string
  label: string
  path: LngLatTuple[]
  color: RgbTuple
  widthPixels: number
  confidence?: number
}

export type DeckTerritoryDatum = {
  id: string
  label: string
  polygon: LngLatTuple[]
  fillColor: RgbaTuple
  lineColor: RgbTuple
  confidence?: number
}

export type RouteTerritoryMapEngineData = {
  mapLibre: {
    center?: LngLatTuple
    bounds?: [southwest: LngLatTuple, northeast: LngLatTuple]
    stops: GeoJsonFeatureCollection<GeoJsonPointFeature>
    routes: GeoJsonFeatureCollection<GeoJsonPathFeature>
    territories: GeoJsonFeatureCollection<GeoJsonPolygonFeature>
  }
  deckGl: {
    stops: DeckStopDatum[]
    routes: DeckRouteDatum[]
    territories: DeckTerritoryDatum[]
  }
}

export type RouteTerritoryMapViewerProps = {
  artifact?: RouteTerritoryMapArtifact | null
  isLoading?: boolean
  error?: string | null
  className?: string
  onMapDataReady?: (data: RouteTerritoryMapEngineData) => void
}

type MapCoordinate = {
  latitude: number
  longitude: number
}

type Point2D = {
  x: number
  y: number
}

type CoordinateBounds = {
  minLatitude: number
  maxLatitude: number
  minLongitude: number
  maxLongitude: number
}

type NormalizedStop = Omit<RouteStop, 'coordinate'> & {
  coordinate: MapCoordinate
  order: number
}

type NormalizedRoute = Omit<RoutePath, 'coordinates'> & {
  id: string
  label: string
  color: string
  coordinates: MapCoordinate[]
  durationMinutes?: number
  distanceKm?: number
}

type NormalizedTerritory = Omit<ServiceTerritory, 'coordinates'> & {
  fill: string
  stroke: string
  coordinates: MapCoordinate[]
}

type NormalizedMap = {
  center?: MapCoordinate
  stops: NormalizedStop[]
  routes: NormalizedRoute[]
  territories: NormalizedTerritory[]
}

type PositionedStop = NormalizedStop & {
  point: Point2D
  labelPoint: Point2D
  labelAnchor: 'start' | 'middle' | 'end'
}

type PositionedRoute = NormalizedRoute & {
  points: Point2D[]
}

type PositionedTerritory = NormalizedTerritory & {
  points: Point2D[]
  labelPoint: Point2D
}

type PositionedMap = {
  tileMap: GeoTileMap
  stops: PositionedStop[]
  routes: PositionedRoute[]
  territories: PositionedTerritory[]
}

type MetricRow = {
  label: string
  value: string
}

type SourceRow = {
  label: string
  value: string
}

type ViewerStateProps = {
  title: string
  message: string
}

type RgbTuple = [red: number, green: number, blue: number]
type RgbaTuple = [red: number, green: number, blue: number, alpha: number]

const svgWidth = 760
const svgHeight = 430
const emptyArtifactMessage = 'No route or territory data is available yet.'
const routePalette = ['#0f766e', '#2563eb', '#b45309', '#be123c']
const territoryPalette = ['#14b8a6', '#3b82f6', '#f59e0b', '#f43f5e']
const stopLabelOffsets = [
  { x: 22, y: -30 },
  { x: -22, y: -30 },
  { x: 24, y: 40 },
  { x: -24, y: 40 },
  { x: 0, y: -50 },
  { x: 0, y: 54 }
] as const
const routeDeckPalette: RgbTuple[] = [
  [15, 118, 110],
  [37, 99, 235],
  [180, 83, 9],
  [190, 18, 60]
]
const territoryDeckPalette: RgbaTuple[] = [
  [20, 184, 166, 58],
  [59, 130, 246, 52],
  [245, 158, 11, 54],
  [244, 63, 94, 48]
]
const territoryLineDeckPalette: RgbTuple[] = [
  [15, 118, 110],
  [37, 99, 235],
  [180, 83, 9],
  [190, 18, 60]
]

// --------------

export default function RouteTerritoryMapViewer({
  artifact,
  isLoading = false,
  error = null,
  className,
  onMapDataReady
}: RouteTerritoryMapViewerProps) {
  const normalizedMap = useMemo(() => (artifact ? normalizeMap(artifact) : null), [artifact])
  const mapEngineData = useMemo(
    () => (normalizedMap ? buildMapEngineData(normalizedMap) : null),
    [normalizedMap]
  )

  useEffect(() => {
    if (!mapEngineData) return

    onMapDataReady?.(mapEngineData)
  }, [mapEngineData, onMapDataReady])

  const wrapperClassName = joinClassNames(
    'not-prose overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-sm',
    className
  )

  if (isLoading) {
    return (
      <section className={wrapperClassName} aria-busy="true">
        <ViewerState title="Loading visual artifact" message="Preparing the map view." />
      </section>
    )
  }

  if (error) {
    return (
      <section className={wrapperClassName} role="alert">
        <ViewerState title="Map unavailable" message={error} />
      </section>
    )
  }

  if (!artifact || !normalizedMap || !hasRenderableData(normalizedMap)) {
    return (
      <section className={wrapperClassName}>
        <ViewerState title="Nothing to show" message={emptyArtifactMessage} />
      </section>
    )
  }

  const positionedMap = positionMap(normalizedMap)
  const metrics = getMetricRows(artifact, normalizedMap)
  const sourceRows = getSourceRows(artifact, normalizedMap)
  const summary = getSummaryLabel(artifact, normalizedMap)

  return (
    <section className={wrapperClassName} aria-label={artifact.title}>
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{artifact.title}</h3>
          <p className="text-muted-foreground text-xs">
            {artifact.type === 'route-map' ? 'Route map' : 'Service territory map'}
          </p>
        </div>
        <span className="border-border text-muted-foreground rounded-md border px-2.5 py-1 text-xs">
          {summary}
        </span>
      </div>

      <div className="bg-muted/25 relative w-full overflow-hidden">
        <svg
          className="block aspect-[16/9] min-h-[320px] w-full"
          role="img"
          aria-label={`${artifact.title} map`}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        >
          <GeoTileLayer tileMap={positionedMap.tileMap} />

          <TerritoryLayer territories={positionedMap.territories} />
          <RouteLayer routes={positionedMap.routes} />
          <StopLayer stops={positionedMap.stops} />
        </svg>
      </div>

      {metrics.length > 0 && <MetricGrid metrics={metrics} />}
      {sourceRows.length > 0 && <SourceNotes rows={sourceRows} />}
    </section>
  )
}

export { RouteTerritoryMapViewer }

// --------------

function TerritoryLayer({ territories }: { territories: PositionedTerritory[] }) {
  return (
    <g>
      {territories.map((territory) => (
        <g key={territory.id}>
          <title>{getTerritoryTitle(territory)}</title>
          <polygon
            points={toPolygonPoints(territory.points)}
            fill={territory.fill}
            fillOpacity="0.2"
            stroke={territory.stroke}
            strokeWidth="2"
          />
          <text
            x={territory.labelPoint.x}
            y={territory.labelPoint.y}
            fill="#0f172a"
            fontSize="12"
            fontWeight="700"
            textAnchor="middle"
          >
            {truncateText(territory.label, 30)}
          </text>
        </g>
      ))}
    </g>
  )
}

function RouteLayer({ routes }: { routes: PositionedRoute[] }) {
  return (
    <g>
      {routes.map((route) => {
        const labelPoint = getMiddlePoint(route.points)

        return (
          <g key={route.id}>
            <title>{getRouteTitle(route)}</title>
            <path
              d={toPath(route.points)}
              fill="none"
              stroke={route.color}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="4"
            />
            <path
              d={toPath(route.points)}
              fill="none"
              stroke="#ffffff"
              strokeDasharray="2 10"
              strokeLinecap="round"
              strokeWidth="2"
            />
            {labelPoint && (
              <text
                x={labelPoint.x}
                y={labelPoint.y - 12}
                fill="#0f172a"
                fontSize="12"
                fontWeight="700"
                paintOrder="stroke"
                stroke="#ffffff"
                strokeWidth="4"
                textAnchor="middle"
              >
                {truncateText(route.label, 26)}
              </text>
            )}
          </g>
        )
      })}
    </g>
  )
}

function StopLayer({ stops }: { stops: PositionedStop[] }) {
  return (
    <g>
      {stops.map((stop) => (
        <g key={stop.id}>
          <title>{getStopTitle(stop)}</title>
          <line
            x1={stop.point.x}
            y1={stop.point.y}
            x2={stop.labelPoint.x}
            y2={stop.labelPoint.y + 6}
            stroke="#0f766e"
            strokeWidth="1.5"
          />
          <circle cx={stop.point.x} cy={stop.point.y} r="13" fill="#0f766e" opacity="0.15" />
          <circle
            cx={stop.point.x}
            cy={stop.point.y}
            r="7"
            fill="#0f766e"
            stroke="#ffffff"
            strokeWidth="2"
          />
          <text
            x={stop.point.x}
            y={stop.point.y + 4}
            fill="#ffffff"
            fontSize="8"
            fontWeight="700"
            textAnchor="middle"
          >
            {String(stop.sequence ?? stop.order + 1).slice(0, 2)}
          </text>
          <text
            x={stop.labelPoint.x}
            y={stop.labelPoint.y}
            fill="#0f172a"
            fontSize="12"
            fontWeight="700"
            paintOrder="stroke"
            stroke="#ffffff"
            strokeWidth="4"
            textAnchor={stop.labelAnchor}
          >
            {truncateText(stop.label, 28)}
          </text>
          {(stop.eta || stop.status || stop.address) && (
            <text
              x={stop.labelPoint.x}
              y={stop.labelPoint.y + 16}
              fill="#475569"
              fontSize="10"
              paintOrder="stroke"
              stroke="#ffffff"
              strokeWidth="3"
              textAnchor={stop.labelAnchor}
            >
              {truncateText(stop.eta || stop.status || stop.address || '', 34)}
            </text>
          )}
        </g>
      ))}
    </g>
  )
}

function MetricGrid({ metrics }: { metrics: MetricRow[] }) {
  return (
    <div className="border-border bg-border grid gap-px border-t sm:grid-cols-2 lg:grid-cols-4">
      {metrics.map((metric) => (
        <div key={`${metric.label}:${metric.value}`} className="bg-background min-w-0 px-4 py-3">
          <p className="text-muted-foreground truncate text-[11px] font-medium uppercase">
            {metric.label}
          </p>
          <p className="text-foreground mt-1 truncate font-mono text-sm font-semibold">
            {metric.value}
          </p>
        </div>
      ))}
    </div>
  )
}

function SourceNotes({ rows }: { rows: SourceRow[] }) {
  return (
    <div className="border-border bg-background space-y-2 border-t px-4 py-3 text-xs">
      {rows.map((row) => (
        <p key={`${row.label}:${row.value}`} className="text-muted-foreground">
          <span className="text-foreground font-semibold">{row.label}: </span>
          {row.value}
        </p>
      ))}
    </div>
  )
}

function ViewerState({ title, message }: ViewerStateProps) {
  return (
    <div className="flex min-h-[320px] items-center justify-center px-6 py-12 text-center">
      <div className="max-w-sm">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-muted-foreground mt-1 text-sm">{message}</p>
      </div>
    </div>
  )
}

// --------------

function normalizeMap(artifact: RouteTerritoryMapArtifact): NormalizedMap {
  const stops = normalizeStops(artifact.data.stops ?? artifact.data.markers)
  const routes = normalizeRoutes(artifact, stops)
  const territories = normalizeTerritories(artifact)
  const center = artifact.data.center ? toCoordinate(artifact.data.center) : undefined

  return {
    center,
    stops,
    routes,
    territories
  }
}

function normalizeStops(stops: RouteStop[] | undefined): NormalizedStop[] {
  return (stops || [])
    .map((stop, index) => ({
      ...stop,
      coordinate: toCoordinate(stop.coordinate),
      order: index
    }))
    .sort((first, second) => (first.sequence ?? first.order) - (second.sequence ?? second.order))
}

function normalizeRoutes(
  artifact: RouteTerritoryMapArtifact,
  stops: NormalizedStop[]
): NormalizedRoute[] {
  const stopsById = new Map(stops.map((stop) => [stop.id, stop.coordinate]))
  const inputRoutes = [
    ...(artifact.data.routes || []),
    ...(artifact.data.paths || []),
    ...(artifact.type === 'route-map' && artifact.data.route
      ? [
          {
            id: 'primary-route',
            label: 'Route',
            coordinates: artifact.data.route
          }
        ]
      : [])
  ]

  const explicitRoutes = inputRoutes
    .map((route, index) => normalizeRoute(route, index, stopsById))
    .filter((route): route is NormalizedRoute => Boolean(route))

  if (explicitRoutes.length > 0 || stops.length < 2) {
    return explicitRoutes
  }

  return [
    {
      id: 'stop-sequence-route',
      label: 'Stop sequence',
      coordinates: stops.map((stop) => stop.coordinate),
      color: routePalette[0]
    }
  ]
}

function normalizeRoute(
  route: RoutePath,
  index: number,
  stopsById: ReadonlyMap<string, MapCoordinate>
): NormalizedRoute | null {
  const coordinates = getRouteCoordinates(route, stopsById)

  if (coordinates.length < 2) {
    return null
  }

  return {
    ...route,
    id: route.id || `route-${index + 1}`,
    label: route.label || `Route ${index + 1}`,
    coordinates,
    color: route.color || routePalette[index % routePalette.length],
    distanceKm: route.distanceKm ?? getMetersAsKm(route.distanceMeters),
    durationMinutes: route.durationMinutes ?? getSecondsAsMinutes(route.durationSeconds)
  }
}

function getRouteCoordinates(
  route: RoutePath,
  stopsById?: ReadonlyMap<string, MapCoordinate>
) {
  const explicitPath = route.coordinates ?? route.path

  if (explicitPath) {
    return explicitPath.map(toCoordinate)
  }

  if (!route.stopIds || !stopsById) {
    return []
  }

  return route.stopIds
    .map((stopId) => stopsById.get(stopId))
    .filter((coordinate): coordinate is MapCoordinate => Boolean(coordinate))
}

function normalizeTerritories(artifact: RouteTerritoryMapArtifact): NormalizedTerritory[] {
  const territories = [...(artifact.data.territories || []), ...(artifact.data.polygons || [])]

  return territories
    .map((territory, index) => {
      const coordinates = territory.coordinates.map(toCoordinate)

      if (coordinates.length < 3) {
        return null
      }

      return {
        ...territory,
        coordinates,
        fill: territory.fill || territory.color || territoryPalette[index % territoryPalette.length],
        stroke: territory.stroke || territory.color || territoryPalette[index % territoryPalette.length]
      }
    })
    .filter((territory): territory is NormalizedTerritory => Boolean(territory))
}

function positionMap(normalizedMap: NormalizedMap): PositionedMap {
  const bounds = getCoordinateBounds(normalizedMap)
  const tileMap = createGeoTileMap(bounds, { width: svgWidth, height: svgHeight })

  return {
    tileMap,
    stops: normalizedMap.stops.map((stop, index) => {
      const point = projectGeoCoordinate(stop.coordinate, tileMap)
      const offset = getStopLabelOffset(index, point)
      const labelPoint = {
        x: clamp(point.x + offset.x, 16, svgWidth - 16),
        y: clamp(point.y + offset.y, 24, svgHeight - 24)
      }
      const labelAnchor: PositionedStop['labelAnchor'] =
        Math.abs(offset.x) < 1 ? 'middle' : offset.x > 0 ? 'start' : 'end'

      return {
        ...stop,
        point,
        labelPoint,
        labelAnchor
      }
    }),
    routes: normalizedMap.routes.map((route) => ({
      ...route,
      points: route.coordinates.map((coordinate) => projectGeoCoordinate(coordinate, tileMap))
    })),
    territories: normalizedMap.territories.map((territory) => {
      const points = territory.coordinates.map((coordinate) => projectGeoCoordinate(coordinate, tileMap))

      return {
        ...territory,
        points,
        labelPoint: getCentroid(points)
      }
    })
  }
}

function getCoordinateBounds(normalizedMap: NormalizedMap): CoordinateBounds {
  const coordinates = [
    ...normalizedMap.stops.map((stop) => stop.coordinate),
    ...normalizedMap.routes.flatMap((route) => route.coordinates),
    ...normalizedMap.territories.flatMap((territory) => territory.coordinates),
    ...(normalizedMap.center ? [normalizedMap.center] : [])
  ]

  return getGeoBounds(coordinates, normalizedMap.center, 0.2)
}

function getStopLabelOffset(index: number, point: Point2D) {
  const baseOffset = stopLabelOffsets[index % stopLabelOffsets.length]
  const nearLeft = point.x < svgWidth * 0.2
  const nearRight = point.x > svgWidth * 0.8
  const nearTop = point.y < svgHeight * 0.22
  const nearBottom = point.y > svgHeight * 0.76
  const x = nearLeft
    ? Math.abs(baseOffset.x) || 24
    : nearRight
      ? -Math.abs(baseOffset.x) || -24
      : baseOffset.x
  const y = nearTop
    ? Math.abs(baseOffset.y)
    : nearBottom
      ? -Math.abs(baseOffset.y)
      : baseOffset.y

  return {
    x,
    y
  }
}

function hasRenderableData(normalizedMap: NormalizedMap) {
  return (
    normalizedMap.stops.length > 0 ||
    normalizedMap.routes.length > 0 ||
    normalizedMap.territories.length > 0
  )
}

// --------------

function buildMapEngineData(normalizedMap: NormalizedMap): RouteTerritoryMapEngineData {
  const bounds = hasRenderableData(normalizedMap) ? getCoordinateBounds(normalizedMap) : undefined
  const center = normalizedMap.center || getCenterFromBounds(bounds)

  return {
    mapLibre: {
      center: center ? toLngLatTuple(center) : undefined,
      bounds: bounds
        ? [
            [bounds.minLongitude, bounds.minLatitude],
            [bounds.maxLongitude, bounds.maxLatitude]
          ]
        : undefined,
      stops: {
        type: 'FeatureCollection',
        features: normalizedMap.stops.map((stop) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: toLngLatTuple(stop.coordinate)
          },
          properties: {
            id: stop.id,
            label: stop.label,
            sequence: stop.sequence,
            status: stop.status,
            confidence: normalizeOptionalConfidence(stop.confidence)
          }
        }))
      },
      routes: {
        type: 'FeatureCollection',
        features: normalizedMap.routes.map((route) => ({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: route.coordinates.map(toLngLatTuple)
          },
          properties: {
            id: route.id,
            label: route.label,
            color: route.color,
            distanceKm: getRouteDistanceKm(route),
            durationMinutes: route.durationMinutes,
            confidence: normalizeOptionalConfidence(route.confidence)
          }
        }))
      },
      territories: {
        type: 'FeatureCollection',
        features: normalizedMap.territories.map((territory) => ({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [closePolygon(territory.coordinates).map(toLngLatTuple)]
          },
          properties: {
            id: territory.id,
            label: territory.label,
            fill: territory.fill,
            stroke: territory.stroke,
            areaSqKm: territory.areaSqKm,
            confidence: normalizeOptionalConfidence(territory.confidence)
          }
        }))
      }
    },
    deckGl: {
      stops: normalizedMap.stops.map((stop) => ({
        id: stop.id,
        label: stop.label,
        position: toLngLatTuple(stop.coordinate),
        color: routeDeckPalette[0],
        radiusMeters: 70,
        confidence: normalizeOptionalConfidence(stop.confidence)
      })),
      routes: normalizedMap.routes.map((route, index) => ({
        id: route.id,
        label: route.label,
        path: route.coordinates.map(toLngLatTuple),
        color: routeDeckPalette[index % routeDeckPalette.length],
        widthPixels: 4,
        confidence: normalizeOptionalConfidence(route.confidence)
      })),
      territories: normalizedMap.territories.map((territory, index) => ({
        id: territory.id,
        label: territory.label,
        polygon: closePolygon(territory.coordinates).map(toLngLatTuple),
        fillColor: territoryDeckPalette[index % territoryDeckPalette.length],
        lineColor: territoryLineDeckPalette[index % territoryLineDeckPalette.length],
        confidence: normalizeOptionalConfidence(territory.confidence)
      }))
    }
  }
}

function getCenterFromBounds(bounds: CoordinateBounds | undefined): MapCoordinate | undefined {
  if (!bounds) return undefined

  return {
    latitude: (bounds.minLatitude + bounds.maxLatitude) / 2,
    longitude: (bounds.minLongitude + bounds.maxLongitude) / 2
  }
}

function closePolygon(coordinates: MapCoordinate[]): MapCoordinate[] {
  const first = coordinates[0]
  const last = coordinates[coordinates.length - 1]

  if (!first || !last || coordinatesMatch(first, last)) {
    return coordinates
  }

  return [...coordinates, first]
}

function coordinatesMatch(first: MapCoordinate, second: MapCoordinate) {
  return first.latitude === second.latitude && first.longitude === second.longitude
}

// --------------

function getMetricRows(
  artifact: RouteTerritoryMapArtifact,
  normalizedMap: NormalizedMap
): MetricRow[] {
  const routeDistanceKm = normalizedMap.routes.reduce(
    (total, route) => total + getRouteDistanceKm(route),
    0
  )
  const routeDurationMinutes = normalizedMap.routes.reduce(
    (total, route) => total + (route.durationMinutes || 0),
    0
  )
  const territoryAreaSqKm = normalizedMap.territories.reduce(
    (total, territory) => total + (territory.areaSqKm || 0),
    0
  )
  const derivedMetrics: MetricRow[] = [
    {
      label: 'Stops',
      value: String(normalizedMap.stops.length)
    },
    {
      label: 'Routes',
      value: String(normalizedMap.routes.length)
    },
    {
      label: 'Territories',
      value: String(normalizedMap.territories.length)
    }
  ]

  if (routeDistanceKm > 0) {
    derivedMetrics.push({
      label: 'Distance',
      value: `${formatNumber(routeDistanceKm)} km`
    })
  }

  if (routeDurationMinutes > 0) {
    derivedMetrics.push({
      label: 'Duration',
      value: formatDuration(routeDurationMinutes)
    })
  }

  if (territoryAreaSqKm > 0) {
    derivedMetrics.push({
      label: 'Service area',
      value: `${formatNumber(territoryAreaSqKm)} sq km`
    })
  }

  const confidence = getAverageConfidence(artifact, normalizedMap)

  if (confidence !== undefined) {
    derivedMetrics.push({
      label: 'Confidence',
      value: formatPercent(confidence)
    })
  }

  return [
    ...derivedMetrics,
    ...normalizedMap.territories.flatMap((territory) =>
      territory.metric
        ? [
            {
              label: territory.metric.label || territory.label,
              value: `${territory.metric.value}${
                territory.metric.unit ? ` ${territory.metric.unit}` : ''
              }`
            }
          ]
        : []
    ),
    ...toMetricRows(artifact.data.summary),
    ...toMetricRows(artifact.data.metrics)
  ].slice(0, 8)
}

function getSourceRows(
  artifact: RouteTerritoryMapArtifact,
  normalizedMap: NormalizedMap
): SourceRow[] {
  const rows: SourceRow[] = []

  if (artifact.data.source) {
    rows.push({
      label: 'Source',
      value: artifact.data.source
    })
  }

  if (artifact.data.confidence !== undefined) {
    rows.push({
      label: 'Artifact confidence',
      value: formatPercent(normalizeConfidence(artifact.data.confidence))
    })
  }

  const sourceNotes = artifact.data.sources || []

  sourceNotes.forEach((source, index) => {
    rows.push({
      label: source.label || `Source ${index + 1}`,
      value: joinClassNames(
        source.source,
        source.note,
        source.confidence !== undefined
          ? `${formatPercent(normalizeConfidence(source.confidence))} confidence`
          : undefined
      )
    })
  })

  artifact.data.notes?.forEach((note, index) => {
    rows.push({
      label: `Note ${index + 1}`,
      value: note
    })
  })

  artifact.data.sourceNotes?.forEach((note, index) => {
    rows.push({
      label: `Source note ${index + 1}`,
      value: note
    })
  })

  normalizedMap.routes.forEach((route) => {
    if (!route.source && route.confidence === undefined) return

    rows.push({
      label: route.label,
      value: joinClassNames(
        route.source,
        route.confidence !== undefined
          ? `${formatPercent(normalizeConfidence(route.confidence))} confidence`
          : undefined
      )
    })
  })

  normalizedMap.territories.forEach((territory) => {
    if (!territory.source && territory.confidence === undefined) return

    rows.push({
      label: territory.label,
      value: joinClassNames(
        territory.source,
        territory.confidence !== undefined
          ? `${formatPercent(normalizeConfidence(territory.confidence))} confidence`
          : undefined
      )
    })
  })

  return rows.filter((row) => row.value.length > 0).slice(0, 6)
}

function getAverageConfidence(
  artifact: RouteTerritoryMapArtifact,
  normalizedMap: NormalizedMap
): number | undefined {
  const values = [
    artifact.data.confidence,
    ...normalizedMap.stops.map((stop) => stop.confidence),
    ...normalizedMap.routes.map((route) => route.confidence),
    ...normalizedMap.territories.map((territory) => territory.confidence)
  ]
    .filter((value): value is number => typeof value === 'number')
    .map(normalizeConfidence)

  if (values.length === 0) return undefined

  return values.reduce((total, value) => total + value, 0) / values.length
}

function getSummaryLabel(artifact: RouteTerritoryMapArtifact, normalizedMap: NormalizedMap) {
  if (artifact.type === 'route-map') {
    return `${normalizedMap.stops.length} stops / ${normalizedMap.routes.length} routes`
  }

  return `${normalizedMap.territories.length} territories / ${normalizedMap.stops.length} stops`
}

function toMetricRows(input: RouteTerritoryMetric[] | RouteTerritoryMetricMap | undefined) {
  if (!input) return []

  if (Array.isArray(input)) {
    return input.map((metric) => ({
      label: metric.label,
      value: `${metric.value}${metric.unit ? ` ${metric.unit}` : ''}`
    }))
  }

  return Object.entries(input).map(([label, value]) => ({
    label,
    value: String(value)
  }))
}

// --------------

function toCoordinate(coordinate: RouteTerritoryCoordinate): MapCoordinate {
  if (Array.isArray(coordinate)) {
    return {
      longitude: coordinate[0],
      latitude: coordinate[1]
    }
  }

  if ('latitude' in coordinate) {
    return {
      latitude: coordinate.latitude,
      longitude: coordinate.longitude
    }
  }

  return {
    latitude: coordinate.lat,
    longitude: coordinate.lng
  }
}

function toLngLatTuple(coordinate: MapCoordinate): LngLatTuple {
  return [coordinate.longitude, coordinate.latitude]
}

function getRouteDistanceKm(route: Pick<NormalizedRoute, 'coordinates' | 'distanceKm'>) {
  if (route.distanceKm !== undefined) return route.distanceKm

  return route.coordinates.reduce((total, coordinate, index) => {
    const previous = route.coordinates[index - 1]

    if (!previous) return total

    return total + getDistanceKm(previous, coordinate)
  }, 0)
}

function getMetersAsKm(value: number | undefined) {
  return value === undefined ? undefined : value / 1000
}

function getSecondsAsMinutes(value: number | undefined) {
  return value === undefined ? undefined : value / 60
}

function getDistanceKm(first: MapCoordinate, second: MapCoordinate) {
  const earthRadiusKm = 6371
  const latitudeDelta = toRadians(second.latitude - first.latitude)
  const longitudeDelta = toRadians(second.longitude - first.longitude)
  const firstLatitude = toRadians(first.latitude)
  const secondLatitude = toRadians(second.latitude)
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(firstLatitude) * Math.cos(secondLatitude) * Math.sin(longitudeDelta / 2) ** 2

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
}

function toRadians(value: number) {
  return (value * Math.PI) / 180
}

function normalizeConfidence(value: number) {
  return clamp(value > 1 ? value / 100 : value, 0, 1)
}

function normalizeOptionalConfidence(value: number | undefined) {
  return value === undefined ? undefined : normalizeConfidence(value)
}

function toPath(points: Point2D[]) {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')
}

function toPolygonPoints(points: Point2D[]) {
  return points.map((point) => `${point.x},${point.y}`).join(' ')
}

function getMiddlePoint(points: Point2D[]) {
  if (points.length === 0) return null

  return points[Math.floor(points.length / 2)]
}

function getCentroid(points: Point2D[]): Point2D {
  if (points.length === 0) {
    return {
      x: svgWidth / 2,
      y: svgHeight / 2
    }
  }

  return {
    x: points.reduce((total, point) => total + point.x, 0) / points.length,
    y: points.reduce((total, point) => total + point.y, 0) / points.length
  }
}

function getStopTitle(stop: PositionedStop) {
  return joinClassNames(
    stop.label,
    stop.address,
    stop.eta ? `ETA ${stop.eta}` : undefined,
    stop.status,
    stop.confidence !== undefined
      ? `${formatPercent(normalizeConfidence(stop.confidence))} confidence`
      : undefined
  )
}

function getRouteTitle(route: PositionedRoute) {
  return joinClassNames(
    route.label,
    `${formatNumber(getRouteDistanceKm(route))} km`,
    route.durationMinutes ? formatDuration(route.durationMinutes) : undefined,
    route.confidence !== undefined
      ? `${formatPercent(normalizeConfidence(route.confidence))} confidence`
      : undefined
  )
}

function getTerritoryTitle(territory: PositionedTerritory) {
  return joinClassNames(
    territory.label,
    territory.areaSqKm ? `${formatNumber(territory.areaSqKm)} sq km` : undefined,
    territory.confidence !== undefined
      ? `${formatPercent(normalizeConfidence(territory.confidence))} confidence`
      : undefined
  )
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en', {
    maximumFractionDigits: value >= 10 ? 1 : 2
  }).format(value)
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${Math.round(minutes)} min`

  const hours = Math.floor(minutes / 60)
  const remainder = Math.round(minutes % 60)

  return remainder > 0 ? `${hours} hr ${remainder} min` : `${hours} hr`
}

function formatPercent(value: number) {
  return `${Math.round(clamp(value, 0, 1) * 100)}%`
}

function truncateText(value: string, maxLength: number) {
  if (value.length <= maxLength) return value

  return `${value.slice(0, Math.max(maxLength - 3, 0))}...`
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function joinClassNames(...values: Array<string | undefined>) {
  return values.filter((value): value is string => Boolean(value)).join(' ')
}
